export function formatMoney(value, currency = 'AUD') {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function formatMonthLabel(monthIso) {
  const [year, month] = monthIso.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
}

export function buildSeries(data) {
  return data.snapshots.map((entry) => ({
    ...entry,
    effectiveBalance: Math.max(entry.loanBalance - entry.offsetBalance, 0),
  }));
}

export function buildSummary(data, series) {
  const first = series[0];
  const last = series[series.length - 1];

  const principalPaid = data.startPrincipal - last.loanBalance;
  const actualPaydownPct = Math.max(0, Math.min((principalPaid / data.startPrincipal) * 100, 100));
  const effectivePaid = data.startPrincipal - last.effectiveBalance;
  const effectiveProgressPct = Math.min((effectivePaid / data.startPrincipal) * 100, 100);
  const offsetCoveragePct = Math.min((last.offsetBalance / last.loanBalance) * 100, 100);
  const fullyOffset = last.offsetBalance >= last.loanBalance;

  const totalInterest = series.reduce((sum, item) => sum + (item.interestPaid || 0), 0);
  const totalExtra = series.reduce((sum, item) => sum + (item.extraRepayment || 0), 0);
  const totalOffsetGrown = Math.max((last?.offsetBalance || 0) - (first?.offsetBalance || 0), 0);

  return {
    first,
    last,
    principalPaid,
    actualPaydownPct,
    totalPrincipalPaidDown: principalPaid,
    effectivePaid,
    effectiveProgressPct,
    offsetCoveragePct,
    fullyOffset,
    totalInterest,
    totalExtra,
    totalOffsetGrown,
  };
}

export function parseCsvSnapshots(csvText) {
  const lines = csvText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return [];
  }

  const parseFlexibleNumber = (raw) => {
    const text = String(raw || '').trim();
    if (!text) {
      return 0;
    }

    const cleaned = text.replace(/\s/g, '');
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const decimalPos = Math.max(lastDot, lastComma);

    if (decimalPos === -1) {
      const digitsOnly = cleaned.replace(/[^\d-]/g, '');
      return Number(digitsOnly || 0);
    }

    const integerPart = cleaned.slice(0, decimalPos).replace(/[^\d-]/g, '');
    const fractionalPart = cleaned.slice(decimalPos + 1).replace(/[^\d]/g, '');
    const normalized = `${integerPart || '0'}.${fractionalPart || '0'}`;
    return Number(normalized);
  };

  const buildSnapshot = (cols, map) => ({
    month: cols[map.month] || '',
    loanBalance: parseFlexibleNumber(cols[map.loanBalance]),
    offsetBalance: parseFlexibleNumber(cols[map.offsetBalance]),
    requiredRepayment: parseFlexibleNumber(cols[map.requiredRepayment]),
    extraRepayment: parseFlexibleNumber(cols[map.extraRepayment]),
    interestPaid: parseFlexibleNumber(cols[map.interestPaid]),
    note: map.note >= 0 ? (cols[map.note] || '') : '',
  });

  const firstCols = lines[0].split(',').map((item) => item.trim());
  const firstLooksLikeMonth = /^\d{4}-\d{2}$/.test(firstCols[0] || '');

  // Support quick pastes without a header row.
  if (firstLooksLikeMonth) {
    return lines.map((line) => {
      const cols = line.split(',').map((item) => item.trim());

      // Schema A: month,loan,offset,required,extra,interest,note
      if (cols.length >= 7) {
        return buildSnapshot(cols, {
          month: 0,
          loanBalance: 1,
          offsetBalance: 2,
          requiredRepayment: 3,
          extraRepayment: 4,
          interestPaid: 5,
          note: 6,
        });
      }

      // Schema B: month,loan,offset,required,interest,note
      return buildSnapshot(cols, {
        month: 0,
        loanBalance: 1,
        offsetBalance: 2,
        requiredRepayment: 3,
        extraRepayment: -1,
        interestPaid: 4,
        note: 5,
      });
    });
  }

  if (lines.length < 2) {
    return [];
  }

  const headers = lines[0].split(',').map((item) => item.trim());
  const idx = {
    month: headers.indexOf('month'),
    loanBalance: headers.indexOf('loanBalance'),
    offsetBalance: headers.indexOf('offsetBalance'),
    requiredRepayment: headers.indexOf('requiredRepayment'),
    extraRepayment: headers.indexOf('extraRepayment'),
    interestPaid: headers.indexOf('interestPaid'),
    note: headers.indexOf('note'),
  };

  if (idx.month === -1 || idx.loanBalance === -1 || idx.offsetBalance === -1) {
    return [];
  }

  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((item) => item.trim());
    return buildSnapshot(cols, idx);
  });
}

function shiftMonth(monthIso, monthsToAdd) {
  const [year, month] = monthIso.split('-').map(Number);
  const date = new Date(year, month - 1 + monthsToAdd, 1);
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${y}-${m}`;
}

function daysInMonth(monthIso) {
  const [year, month] = monthIso.split('-').map(Number);
  return new Date(year, month, 0).getDate();
}

export function averageMonthlyMovement(series) {
  if (series.length < 2) {
    return {
      principalDrop: 0,
      offsetGain: 0,
    };
  }

  const deltas = series.slice(1).map((row, idx) => {
    const prev = series[idx];
    return {
      principalDrop: Math.max(prev.loanBalance - row.loanBalance, 0),
      offsetGain: Math.max(row.offsetBalance - prev.offsetBalance, 0),
    };
  });

  const totals = deltas.reduce(
    (acc, item) => ({
      principalDrop: acc.principalDrop + item.principalDrop,
      offsetGain: acc.offsetGain + item.offsetGain,
    }),
    { principalDrop: 0, offsetGain: 0 }
  );

  return {
    principalDrop: totals.principalDrop / deltas.length,
    offsetGain: totals.offsetGain / deltas.length,
  };
}

export function buildScenarioForecasts(series, options = {}) {
  if (!series.length) {
    return [];
  }

  const scenarios =
    Array.isArray(options.offsetScenarios) && options.offsetScenarios.length
      ? options.offsetScenarios
      : [
          { id: 'no-growth', label: 'No offset growth', color: '#aa5d27', offsetGain: 0 },
          { id: 'scenario-a', label: 'Offset scenario A', color: '#1f7a73', offsetGain: 0 },
          { id: 'scenario-b', label: 'Offset scenario B', color: '#2b4f97', offsetGain: 0 },
        ];

  const last = series[series.length - 1];
  const avg = averageMonthlyMovement(series);
  const maxMonths = 360;
  const annualInterestRatePct = Number(options.annualInterestRatePct);
  const minimumMonthlyRepayment = Number(options.minimumMonthlyRepayment);
  const useInterestModel =
    Number.isFinite(annualInterestRatePct) &&
    annualInterestRatePct >= 0 &&
    Number.isFinite(minimumMonthlyRepayment) &&
    minimumMonthlyRepayment > 0;

  const averageExtraRepayment =
    series.reduce((sum, row) => sum + (row.extraRepayment || 0), 0) / Math.max(series.length, 1);

  return scenarios.map((scenario) => {
    const principalDropTarget = Math.max(avg.principalDrop, 1);
    const rawOffsetGain = Number(scenario.offsetGain);
    const offsetGain = Number.isFinite(rawOffsetGain) && rawOffsetGain >= 0 ? rawOffsetGain : 0;

    let loan = last.loanBalance;
    let offset = last.offsetBalance;
    const points = [];
    let totalPrincipalDrop = 0;

    const scenarioMonthlyRepayment = useInterestModel
      ? minimumMonthlyRepayment + averageExtraRepayment
      : null;

    for (let i = 1; i <= maxMonths; i += 1) {
      const month = shiftMonth(last.month, i);
      const previousLoan = loan;

      if (useInterestModel) {
        const dailyRate = annualInterestRatePct / 100 / 365;
        const days = daysInMonth(month);
        const interestBearingBalance = Math.max(loan - offset, 0);
        const monthlyInterest = interestBearingBalance * (Math.pow(1 + dailyRate, days) - 1);
        loan = Math.max(loan + monthlyInterest - scenarioMonthlyRepayment, 0);
      } else {
        loan = Math.max(loan - principalDropTarget, 0);
      }

      offset = offset + offsetGain;
      const effectiveBalance = Math.max(loan - offset, 0);
      totalPrincipalDrop += Math.max(previousLoan - loan, 0);
      points.push({
        month,
        loanBalance: loan,
        offsetBalance: offset,
        effectiveBalance,
      });
      if (effectiveBalance <= 0) {
        break;
      }
    }

    const payoffPoint = points[points.length - 1];
    const fullyOffsetReached = payoffPoint?.effectiveBalance <= 0;

    return {
      ...scenario,
      points,
      principalDrop: totalPrincipalDrop / Math.max(points.length, 1),
      offsetGain,
      useInterestModel,
      annualInterestRatePct: useInterestModel ? annualInterestRatePct : null,
      minimumMonthlyRepayment: useInterestModel ? minimumMonthlyRepayment : null,
      scenarioMonthlyRepayment,
      monthsToFullyOffset: fullyOffsetReached ? points.length : null,
      payoffMonth: fullyOffsetReached ? payoffPoint.month : null,
    };
  });
}